import { useEffect } from 'react';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { newRelic } from '~/utils/newRelic/newRelic';
import { safeParseWithLogging } from '~/utils/zodHelpers';

import { INVOICE_ITEMS_CACHE_KEY } from '../constants/invoicePage.const';
import { INVOICE_ITEMS_RESPONSE_SCHEMA } from '../schemata/invoice.schema';
import * as invoicesService from '../services/invoices.service';
import { type InvoiceItemsResponse } from '../types/invoice.type';

export const useFetchInvoiceItems = (
  invoiceId: string,
): UseQueryResult<InvoiceItemsResponse, AxiosError> => {
  const query = useQuery<InvoiceItemsResponse, AxiosError>({
    queryFn: async () => {
      const response = await invoicesService.getInvoiceItemsOfInvoice(invoiceId);
      return safeParseWithLogging(response, INVOICE_ITEMS_RESPONSE_SCHEMA);
    },
    queryKey: [invoiceId, INVOICE_ITEMS_CACHE_KEY],
  });

  useEffect(() => {
    if (query.error)
      newRelic.noticeError('Error while fetching invoice items', { error: query.error });
  }, [query.error]);

  return query;
};
